import { useCallback, useEffect, useState } from 'react'
import { breakpoints } from 'utils/styles/breakpoints'

export interface VisibleSlidesConfig {
  cardWidths: {
    MOBILE: number
    TABLET: number
    DESKTOP: number
  }
  config: {
    CARD_MARGIN: number
    CARD_MARGIN_MD_UP: number
    DEFAULT_VISIBLE_CARDS: number
    MIN_VISIBLE_CARDS: number
    RESIZE_DEBOUNCE_MS?: number
    SCROLL_THRESHOLD?: number
  }
  currentCardSlidesShown?: number | null
}

export interface VisibleSlidesResult {
  visibleSlides: number
  slidesToScroll: number
  calculateVisibleSlides: () => number
  calculateSlidesToScroll: (visible?: number) => number
}

export const useVisibleSlidesCalculation = (
  containerRef: React.RefObject<HTMLDivElement>,
  { cardWidths, config, currentCardSlidesShown }: VisibleSlidesConfig
): VisibleSlidesResult => {
  const [visibleSlides, setVisibleSlides] = useState(config.DEFAULT_VISIBLE_CARDS)
  const [slidesToScroll, setSlidesToScroll] = useState(config.DEFAULT_VISIBLE_CARDS)
  // Calculate how many slides can be visible based on viewport size
  const calculateVisibleSlides = useCallback(() => {
    if (currentCardSlidesShown) return currentCardSlidesShown

    if (!containerRef.current) return config.DEFAULT_VISIBLE_CARDS
    const containerWidth = containerRef.current.offsetWidth
    const minWidth = cardWidths.MOBILE * 2 + config.CARD_MARGIN * 2

    if (containerWidth < minWidth) {
      return config.MIN_VISIBLE_CARDS
    }
    // We have different card sizes for different breakpoints
    // So we calculate the "visible" slides based off the width
    // of the cards
    let cardWithMargin: number
    const viewportWidth = window.innerWidth
    if (viewportWidth >= Number(breakpoints.md)) {
      // Card transitions at 1128 to desktop
      cardWithMargin = cardWidths.DESKTOP + config.CARD_MARGIN_MD_UP
    } else if (viewportWidth >= Number(breakpoints.sm)) {
      // Card transitions at 768 to tablet
      cardWithMargin = cardWidths.TABLET + config.CARD_MARGIN
    } else {
      cardWithMargin = cardWidths.MOBILE + config.CARD_MARGIN
    }

    const availableWidth = containerWidth
    const visible = availableWidth / cardWithMargin

    return visible
  }, [containerRef, cardWidths, config, currentCardSlidesShown])

  // Calculate how many slides to scroll based on visible slides
  const calculateSlidesToScroll = useCallback(
    (visible?: number) => {
      const currentVisible = visible ?? calculateVisibleSlides()
      const threshold = config.SCROLL_THRESHOLD ?? 3
      // Scroll full set on larger screens, single slides on smaller screens
      return currentVisible >= threshold ? Math.floor(currentVisible) : 1
    },
    [calculateVisibleSlides, config.SCROLL_THRESHOLD]
  )

  // Debounced resize handler
  // To avoid performance issues on rapid resize events
  useEffect(() => {
    // Debounced resize listener with proper cleanup to prevent race conditions
    let timeoutId: NodeJS.Timeout

    const updateSlideCalculations = () => {
      const newVisibleSlides = calculateVisibleSlides()
      const newSlidesToScroll = calculateSlidesToScroll(newVisibleSlides)
      setVisibleSlides(newVisibleSlides)
      setSlidesToScroll(newSlidesToScroll)
    }
    // Initial calculation (after layout)
    updateSlideCalculations()
    // Fallback: recalculate after a short timeout in case layout wasn't ready
    const fallbackTimeout = setTimeout(updateSlideCalculations, 100)

    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateSlideCalculations, config.RESIZE_DEBOUNCE_MS || 100)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
      clearTimeout(fallbackTimeout)
    }
  }, [calculateVisibleSlides, calculateSlidesToScroll, config.RESIZE_DEBOUNCE_MS])

  return {
    visibleSlides,
    slidesToScroll,
    calculateVisibleSlides,
    calculateSlidesToScroll
  }
}
